// src/components/AuthModal.js
import React, { useState } from 'react';
import { X, User, Lock, Mail } from 'lucide-react';
import GoogleLoginButton from './GoogleLoginButton';
import useApi from '../hooks/useApi'; // <<<--- IMPORTA EL HOOK PERSONALIZADO

// Recibe onLoginSuccess de Landing.js
const AuthModal = ({ onClose, onLoginSuccess }) => { // Ya no necesitamos onRegisterSuccess aquí
  const [mode, setMode] = useState('login');
  const [formError, setFormError] = useState(''); // Estado unificado para errores de formulario

  // Estados para los campos de los formularios
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');

  // --- Instancias del Hook useApi ---
  // Una para la operación de login
  const { loading: loginLoading, request: performLogin } = useApi();
  // Otra para la operación de registro
  const { loading: registerLoading, request: performRegister } = useApi();

  // Combinar estados de carga para deshabilitar botones si CUALQUIERA está cargando
  const isLoading = loginLoading || registerLoading;

  // --- Funciones de Submit (Usando el Hook useApi) ---
  const handleLoginSubmit = async (e) => {
     e.preventDefault();
     setFormError(''); // Limpiar errores previos del formulario

     try {
        // Llama a la función 'request' devuelta por el hook useApi
        const loginData = await performLogin('/api/login', { // Endpoint y opciones
           method: 'POST',
           body: JSON.stringify({ username: loginUsername, password: loginPassword }),
        });

        // Si la petición fue exitosa (2xx) y tenemos un token
        if (loginData?.access_token) {
           onLoginSuccess(loginData.access_token); // Llama al callback de éxito de Landing
        } else {
           // Caso raro: éxito 2xx pero sin token
           console.warn("Login successful (2xx) but no access_token found:", loginData);
           setFormError('Respuesta inesperada del servidor. Inténtalo de nuevo.');
        }
     } catch (err) {
        // El hook 'useApi' ya manejó el error de fetch/status y lo lanzó.
        // Aquí solo decidimos qué mostrar al usuario.
        console.error("Login failed in component:", err);
        if (err.status === 403 && err.data?.error?.toLowerCase().includes("verificar tu correo")) {
            setFormError("Debes verificar tu correo electrónico. Revisa tu bandeja de entrada.");
            // Considerar añadir un botón "Reenviar correo" que llame a un nuevo endpoint
        } else {
            // Muestra el error específico del backend si existe, si no, el mensaje genérico del error
            setFormError(err.data?.error || err.message || 'Error al iniciar sesión. Verifica credenciales.');
        }
     }
     // No necesitamos setIsLoading(false), el hook lo gestiona
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    try {
        await performRegister('/api/register', {
           method: 'POST',
           body: JSON.stringify({
             username: registerUsername,
             email: registerEmail,
             password: registerPassword
           }),
        });
        // Si performRegister NO lanzó un error, significa que la API devolvió 201 Created
        setFormError('¡Registro exitoso! Revisa tu correo para verificar tu cuenta antes de iniciar sesión.');
        setMode('login'); // Cambiar a la pestaña de login
        // Limpiar campos de registro
        setRegisterUsername('');
        setRegisterEmail('');
        setRegisterPassword('');

    } catch (err) {
        // Captura el error lanzado por el hook useApi
        console.error("Register failed in component:", err);
        // Muestra el mensaje de error específico del backend o uno genérico
        setFormError(err.data?.error || err.message || 'Error en el registro. Inténtalo de nuevo.');
    }
     // No necesitamos setIsLoading(false), el hook lo gestiona
  };

  // --- Manejo de Error de Google ---
  // Callback para manejar errores desde GoogleLoginButton
  const handleGoogleError = (googleErrorMessage) => {
     setFormError(googleErrorMessage || 'Error al iniciar sesión con Google.');
     // Podríamos querer asegurarnos que loginLoading/registerLoading sean false
     // aunque el hook debería manejarlos individualmente.
  }


  // --- Renderizado del Componente (JSX) ---
  return (
    // Overlay y contenedor del modal
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 animate-fade-in">
      <div className="relative bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 m-4">
        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 rounded-full text-gray-500 hover:text-white hover:bg-gray-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
          aria-label="Cerrar modal"
        >
          <X size={20} />
        </button>

        {/* Tabs para Login/Registro */}
        <div className="flex mb-6 border-b border-gray-700">
          <button
            onClick={() => { setMode('login'); setFormError(''); }} // Limpiar error al cambiar de tab
            className={`flex-1 py-3 text-sm sm:text-base text-center font-medium transition-colors duration-200 ${
              mode === 'login'
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-200 border-b-2 border-transparent'
            }`}
          >
            Iniciar Sesión
          </button>
          <button
            onClick={() => { setMode('register'); setFormError(''); }} // Limpiar error al cambiar de tab
            className={`flex-1 py-3 text-sm sm:text-base text-center font-medium transition-colors duration-200 ${
              mode === 'register'
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-200 border-b-2 border-transparent'
            }`}
          >
            Registrarse
          </button>
        </div>

        {/* Mensaje de Error/Notificación Unificado */}
        {formError && (
            <p className={`mb-4 text-sm text-center font-medium ${
               formError.includes('Revisa tu correo') ? 'text-yellow-400' :
               formError.includes('exitoso') ? 'text-green-400' :
               'text-red-400' // Errores generales
            }`}>
              {formError}
            </p>
         )}

        {/* --- Formularios --- */}
        {mode === 'login' ? (
           // --- Formulario de Login ---
           <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                 <label htmlFor="login-username" className="block text-sm font-medium text-gray-300 mb-1.5">Usuario</label>
                 <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    <input id="login-username" type="text" value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm" placeholder="Tu nombre de usuario" />
                 </div>
               </div>
               <div>
                 <label htmlFor="login-password"className="block text-sm font-medium text-gray-300 mb-1.5">Contraseña</label>
                 <div className="relative">
                   <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                   <input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm" placeholder="••••••••" />
                 </div>
               </div>
               {/* Usa el estado de carga combinado 'isLoading' */}
               <button type="submit" disabled={isLoading} className={`w-full group py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white font-bold relative overflow-hidden transition-opacity duration-200 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:from-purple-700 hover:to-pink-700'}`}>
                 {/* Muestra el estado de carga específico de login */}
                 <span className="relative z-10 flex items-center justify-center text-base">{loginLoading ? 'Iniciando...' : 'Iniciar Sesión'}</span>
                 {!isLoading && <span className="absolute inset-0 bg-gradient-to-r from-purple-700 to-pink-700 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>}
               </button>
           </form>
        ) : (
           // --- Formulario de Registro ---
           <form onSubmit={handleRegisterSubmit} className="space-y-4">
             <div>
                 <label htmlFor="register-username" className="block text-sm font-medium text-gray-300 mb-1.5">Usuario</label>
                 <div className="relative">
                   <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                   <input id="register-username" type="text" value={registerUsername} onChange={(e) => setRegisterUsername(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm" placeholder="Elige un nombre de usuario" />
                 </div>
               </div>
               <div>
                 <label htmlFor="register-email" className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                 <div className="relative">
                   <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                   <input id="register-email" type="email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm" placeholder="tu@email.com" />
                 </div>
               </div>
               <div>
                 <label htmlFor="register-password"className="block text-sm font-medium text-gray-300 mb-1.5">Contraseña</label>
                 <div className="relative">
                   <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                   <input id="register-password" type="password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} required minLength={6} className="w-full pl-10 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm" placeholder="Crea una contraseña segura" />
                 </div>
               </div>
               {/* Usa el estado de carga combinado 'isLoading' */}
               <button type="submit" disabled={isLoading} className={`w-full group py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white font-bold relative overflow-hidden transition-opacity duration-200 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:from-purple-700 hover:to-pink-700'}`}>
                 {/* Muestra el estado de carga específico de registro */}
                 <span className="relative z-10 flex items-center justify-center text-base">{registerLoading ? 'Registrando...' : 'Crear Cuenta'}</span>
                 {!isLoading && <span className="absolute inset-0 bg-gradient-to-r from-purple-700 to-pink-700 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>}
               </button>
           </form>
        )}

        {/* --- Divisor "O" y Botón de Google --- */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-700/50" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-gray-900 text-gray-400">O continúa con</span>
          </div>
        </div>

        {/* --- Área para el Botón de Google --- */}
        {/* Asegúrate que GoogleLoginButton maneje su propio estado de carga interno si es necesario */}
        <div className="mt-4">
           <GoogleLoginButton
             onSuccess={onLoginSuccess} // Pasa la función de éxito de Landing
             onErrorCallback={handleGoogleError} // Pasa la función local para manejar errores
           />
        </div>

      </div> {/* Fin Contenedor Interior Modal */}
    </div> // Fin Overlay Modal
  );
};

export default AuthModal;